import React from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { Link } from "react-router-dom";

export default function SingleVerticalPage() {
  return (
    <div className="single-vertical-parent">
      <div className="vertical-title">
        <div className="text-900 font-bold text-7xl mb-4 text-center">
          FlexNet Manufacturing: Driving Smart Factories with 5G Solutions
        </div>
        <div className="text-700 text-xl mb-6 text-center line-height-3">
          Unlock the future of manufacturing with seamless connectivity,
          real-time monitoring, and automation.
        </div>
      </div>
      {/* Hero Section */}
      <div className="grid grid-nogutter surface-section text-800">
        <div className="col-12 md:col-6 p-6 text-center md:text-left flex align-items-center">
          <section>
            <span className="block text-6xl font-bold mb-3">
              FlexNet Manufacturing
            </span>
            <div className="text-6xl text-primary font-bold mb-3">
              Driving Smart Factories with 5G Solutions
            </div>
            <p className="mt-0 mb-4 text-700 line-height-3">
              Transform your manufacturing operations with FlexNet’s private 5G
              networks. Improve productivity, enhance worker safety, and
              streamline automation with our cutting-edge solutions.
            </p>
            <button className="flex underline align-items-center mt-4">
              <Link to="/connectivity">Request Demo</Link>
              <FaExternalLinkAlt className="ml-2" />
            </button>
          </section>
        </div>
        <div className="col-12 md:col-6 overflow-hidden">
          <img
            src="https://media.istockphoto.com/id/1341262391/photo/automated-robot-carriers-and-robotic-arm-in-smart-distribution-warehouse.jpg?s=612x612&w=0&k=20&c=bQriL_lPl8-f55-sXTsGiUr9fWlocmWWXM-6U3tn0DU="
            alt="hero-1"
            className="md:ml-auto block md:h-full"
            style={{ clipPath: "polygon(8% 0, 100% 0%, 100% 100%, 0 100%)" }}
          />
        </div>
      </div>

      {/* Key Benefits Section */}
      <div className="surface-section px-4 py-8 md:px-6 lg:px-8 text-center">
        <div className="mb-3 font-bold text-3xl">
          <span className="text-900">Why </span>
          <span className="text-blue-600">FlexNet for Manufacturing?</span>
        </div>
        <div className="grid">
          {keyBenefits.map((benefit, index) => (
            <div key={index} className="col-12 md:col-4 mb-4 px-5">
              <span
                className="p-3 shadow-2 mb-3 inline-block surface-card"
                style={{ borderRadius: "10px" }}
              >
                <i className={`${benefit.icon} text-4xl text-blue-500`}></i>
              </span>
              <div className="text-900 text-xl mb-3 font-medium">
                {benefit.title}
              </div>
              <span className="text-700 line-height-3">{benefit.detail}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Use Cases Section */}

      <div className="mb-3 font-bold text-3xl">
        <span className="text-900">FlexNet Manufacturing </span>
        <span className="text-blue-600">Use Cases</span>
      </div>
      {useCases.map((useCase, index) => (
        <div
          key={index}
          className="surface-section px-4 py-8 md:px-6 lg:px-8 text-center"
        >
          <div className="b-3 font-bold text-2xl mb-3">
            {" "}
            <span className="text-blue-600">Use Case</span>
            {useCase.title}
          </div>
          <div className="text-700 mb-6">{useCase.description}</div>
          <div className="grid">
            <div className="col-12 md:col-4 mb-4 px-5">
              <div className="text-900 text-xl mb-3 font-medium">Benefits</div>
              {useCase.benefits.map((item, idx) => (
                <div key={idx} className="mb-2">
                  <i className={`${item.icon} text-blue-500 mr-2`}></i>
                  {item.title}
                </div>
              ))}
            </div>
            <div className="col-12 md:col-4 mb-4 px-5">
              <div className="text-900 text-xl mb-3 font-medium">
                Devices/Technologies
              </div>
              {useCase.devices.map((item, idx) => (
                <div key={idx} className="mb-2">
                  <i className={`${item.icon} text-blue-500 mr-2`}></i>
                  {item.title}
                </div>
              ))}
            </div>
            <div className="col-12 md:col-4 mb-4 px-5">
              <div className="text-900 text-xl mb-3 font-medium">
                Applications
              </div>
              {useCase.applications.map((item, idx) => (
                <div key={idx} className="mb-2">
                  <i className={`${item.icon} text-blue-500 mr-2`}></i>
                  {item.title}
                </div>
              ))}
            </div>

            <button className="flex underline align-items-center mt-4 vertical-market-link">
              <Link to="/marketplace">Request Access to Marketplace</Link>
              <FaExternalLinkAlt className="ml-2" />
            </button>
          </div>
        </div>
      ))}

      {/* Call-to-Action Section */}
      <div className="surface-section text-center py-8">
        <div className="text-900 text-3xl font-bold mb-4">
          Ready to Transform Your Factory?
        </div>

        <button className="p-button p-button-raised connectivity-request-demo-button p-3">
          <Link to="contact-page">Get in Touch for a Personalized Demo</Link>
        </button>
      </div>
    </div>
  );
}

const useCases = [
  {
    title: " 1: Real-Time Monitoring with AIoT Cameras",
    description:
      "Leverage AIoT cameras for real-time monitoring of production lines, ensuring operational efficiency and safety.",
    benefits: [
      {
        title: "Immediate detection of anomalies",
        icon: "pi pi-exclamation-circle",
      },
      { title: "Enhanced quality control", icon: "pi pi-check-circle" },
      { title: "Improved worker safety", icon: "pi pi-shield" },
    ],
    devices: [
      { title: "5G AIoT Cameras", icon: "pi pi-camera" },
      { title: "Industrial Routers", icon: "pi pi-globe" },
    ],
    applications: [
      {
        title: "Visual AI Software for real-time analysis",
        icon: "pi pi-desktop",
      },
    ],
  },

  {
    title: " 2: Predictive Maintenance with IoT Sensors",
    description:
      "Utilize IoT sensors to monitor machinery health and predict potential failures before they occur.",
    benefits: [
      { title: "Reduced Downtime", icon: "pi pi-clock" },
      { title: "Lower Maintenance Costs", icon: "pi pi-dollar" },
      { title: "Extended Equipment Lifespan", icon: "pi pi-cog" },
    ],
    devices: [
      { title: "Vibration Sensors", icon: "pi pi-sliders-h" },
      { title: "Temperature Sensors", icon: "pi pi-sun" },
      { title: "Pressure Sensors", icon: "pi pi-gauge" },
    ],
    applications: [
      { title: "Predictive Maintenance Systems", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 3: Automated Guided Vehicles (AGVs) for Material Handling",
    description:
      "Integrate 5G-enabled AGVs for transporting raw materials and finished goods across the production floor efficiently.",
    benefits: [
      { title: "Optimized material flow", icon: "pi pi-sync" },
      { title: "Reduced human intervention", icon: "pi pi-user-minus" },
      { title: "Real-time route optimization", icon: "pi pi-map" },
    ],
    devices: [
      {
        title: "AGVs with LiDAR, Ultrasonic, and Proximity Sensors",
        icon: "pi pi-car",
      },
    ],
    applications: [
      {
        title: "Warehouse Management Systems, AI-Powered Routing Software",
        icon: "pi pi-server",
      },
    ],
  },
  {
    title: " 4: Robotic Automation & Quality Inspection",
    description:
      "Deploy collaborative robots (Cobots) and automated quality inspection systems for precise manufacturing and defect detection.",
    benefits: [
      { title: "Increased production speed", icon: "pi pi-forward" },
      { title: "Higher product quality", icon: "pi pi-star" },
      { title: "Reduced manual errors", icon: "pi pi-times-circle" },
    ],
    devices: [
      {
        title: "3D Cameras, Laser Scanners, Force-Torque Sensors",
        icon: "pi pi-camera",
      },
    ],
    applications: [
      {
        title:
          "AI-Based Defect Detection Software, Robotic Process Automation Tools",
        icon: "pi pi-desktop",
      },
    ],
  },
  {
    title: " 5: Smart Energy Management",
    description:
      "Optimize energy consumption in factories with real-time monitoring and AI-driven energy management systems.",
    benefits: [
      { title: "Lower energy costs", icon: "pi pi-dollar" },
      { title: "Sustainable operations", icon: "pi pi-heart  " },
      { title: "Real-time consumption insights", icon: "pi pi-chart-bar" },
    ],
    devices: [
      {
        title: "Smart Meters, IoT Energy Sensors, Edge Computing",
        icon: "pi pi-bolt",
      },
    ],
    applications: [
      { title: "Smart Energy Management Platforms", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 6: AI Computer Vision for Visual Inspection",
    description:
      "AI-enabled cameras connected via 5G detect defects on parts and vehicles in real-time.",
    benefits: [
      { title: "High-resolution defect detection", icon: "pi pi-eye" },
      {
        title: "Real-time anomaly identification",
        icon: "pi pi-exclamation-circle",
      },
      { title: "AI-powered classification", icon: "pi pi-lightbulb " },
    ],
    devices: [{ title: "5G Cameras, Vision Sensors", icon: "pi pi-camera" }],
    applications: [
      { title: "Vision Analytics Software", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 7: Dynamic HVAC Optimization",
    description:
      "AI-powered systems adjust HVAC settings dynamically based on real-time occupancy and environmental data.",
    benefits: [
      { title: "Optimized energy usage", icon: "pi pi-bolt" },
      { title: "Enhanced workplace comfort", icon: "pi pi-home" },
      { title: "Cost savings", icon: "pi pi-dollar" },
    ],
    devices: [
      { title: "Environmental Sensors, IoT Gateways", icon: "pi pi-cloud" },
    ],
    applications: [
      { title: "Environmental Monitoring Platforms", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 8: Worker Safety and Health Monitoring",
    description:
      "Using IoT wearables to track worker vital signs and detect anomalies in real-time.",
    benefits: [
      { title: "Enhanced Worker Safety", icon: "pi pi-shield" },
      {
        title: "Immediate Incident Detection",
        icon: "pi pi-exclamation-circle",
      },
      { title: "Proactive Health Management", icon: "pi pi-heart" },
    ],
    devices: [
      { title: "Wearables (e.g., smart bands, helmets)", icon: "pi pi-mobile" },
      { title: "IoT Gateways", icon: "pi pi-cloud" },
    ],
    applications: [
      { title: "Worker Safety Monitoring Systems", icon: "pi pi-desktop" },
    ],
  },
];

const keyBenefits = [
  {
    title: "Reliable 4G/5G Connectivity",
    detail: "Ensure seamless and secure network operations.",
    icon: "pi pi-wifi",
  },
  {
    title: "Real-Time Asset Tracking",
    detail: "Monitor and manage assets efficiently.",
    icon: "pi pi-map-marker",
  },
  {
    title: "Enhanced Worker Safety",
    detail: "Use IoT-enabled devices to ensure safety.",
    icon: "pi pi-shield",
  },
  {
    title: "Seamless Automation & AGVs",
    detail: "Integrate autonomous systems effortlessly.",
    icon: "pi pi-cog",
  },
  {
    title: "Predictive Maintenance",
    detail: "Increase equipment longevity with AI-driven monitoring.",
    icon: "pi pi-chart-line",
  },
  {
    title: "Centralized Network Control",
    detail: "Manage networks with a single control hub.",
    icon: "pi pi-globe",
  },
];
